import React from 'react';
import { formatDate, isSameDay, addDays, eachDayOfInterval } from '../../../utils/dateUtils';

/**
 * Get the next 28 days starting from today
 */
const getNextTwentyEightDays = () => {
  const today = new Date();
  const end = addDays(today, 27);
  return eachDayOfInterval({ start: today, end });
};

/**
 * Get classes for a specific day
 */
const getClassesForDay = (classes, date) => {
  return classes.filter(cls => {
    const classDate = new Date(new Date(cls.startTime).setHours(0, 0, 0, 0));
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    return isSameDay(classDate, targetDate);
  });
};

/**
 * Get header text showing the date range
 */
const getCalendarHeaderText = () => {
  const dates = getNextTwentyEightDays();
  const startDate = dates[0];
  const endDate = dates[dates.length - 1];
  
  const startMonth = formatDate(startDate, 'MMMM');
  const startYear = formatDate(startDate, 'yyyy');
  const endMonth = formatDate(endDate, 'MMMM');
  const endYear = formatDate(endDate, 'yyyy');
  
  if (startMonth === endMonth && startYear === endYear) {
    return `${startMonth} ${startYear}`;
  } else if (startYear === endYear) {
    return `${startMonth} - ${endMonth} ${startYear}`;
  } else {
    return `${startMonth} ${startYear} - ${endMonth} ${endYear}`;
  }
};

/**
 * Individual class card in the calendar
 */
const ClassCard = ({ classItem, onClick, isUserBooked, isUserOnWaitlist }) => {
  const booked = isUserBooked(classItem);
  const onWaitlist = isUserOnWaitlist(classItem);

  return (
    <div
      onClick={() => onClick(classItem)}
      className="text-xs text-secondary transition-all duration-200 p-1 rounded relative cursor-pointer"
    >
      <div className="flex-1 rounded transition-all duration-200 hover:bg-hover p-2 calendar-class-card relative flex flex-col">
        {/* Booking Badge */}
        {booked && (
          <div className="class-card-booking-badge">
            <span className="badge badge-booked">Booked</span>
          </div>
        )}

        {/* Waitlist Badge */}
        {onWaitlist && classItem.isFull && (
          <div className="class-card-waitlist-badge">
            <span className="badge badge-waitlist">Waitlisted</span>
          </div>
        )}

        <div className="font-medium mb-1">{classItem.name}</div>
        <div className="text-muted mb-1" style={{ fontSize: '11px' }}>
          {classItem.instructor}
        </div>
        <div className="text-tertiary mb-1">
          {formatDate(classItem.startTime, 'h:mm a')}
        </div>

        {/* Availability Tag */}
        {!booked && (!onWaitlist || (onWaitlist && !classItem.isFull)) && (
          <div className="text-xs">
            {classItem.isFull ? (
              <span className="badge badge-waitlist-join">Join Waitlist</span>
            ) : (
              <span className="badge badge-available">{classItem.availableSpots} spots</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Calendar day cell
 */
const CalendarDay = ({ date, index, classes, onClassClick, isUserBooked, isUserOnWaitlist }) => {
  const dayClasses = getClassesForDay(classes, date);
  const isToday = isSameDay(date, new Date());
  const allDates = getNextTwentyEightDays();
  const isFirstOfMonth = index === 0 || formatDate(date, 'MMM yyyy') !== formatDate(allDates[index - 1], 'MMM yyyy');

  return (
    <div
      className={`calendar-day-card p-2 border-b border-primary ${
        (index + 1) % 7 === 0 ? '' : 'border-r border-primary'
      } ${isToday ? 'calendar-today' : 'bg-card'}`}
    >
      {/* Date Header */}
      <div className="flex justify-between items-start mb-2">
        <div className="text-xs font-medium text-tertiary">
          {formatDate(date, 'MMM').toUpperCase()} {date.getDate()}
          {isFirstOfMonth && formatDate(date, 'yyyy') !== formatDate(new Date(), 'yyyy') && ` ${formatDate(date, 'yyyy')}`}
        </div>
        <div className="text-xs font-medium text-tertiary">
          {formatDate(date, 'EEE').toUpperCase()}
        </div>
      </div>

      {/* Classes */}
      {dayClasses.length > 0 ? (
        <div className="space-y-0">
          {dayClasses.map((cls, classIndex) => (
            <div
              key={cls._id}
              className={classIndex < dayClasses.length - 1 ? 'border-b border-primary' : ''}
            >
              <ClassCard
                classItem={cls}
                onClick={onClassClick}
                isUserBooked={isUserBooked}
                isUserOnWaitlist={isUserOnWaitlist}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-muted text-center mt-4" />
      )}
    </div>
  );
};

/**
 * Calendar view component
 */
const CalendarView = ({ classes, onClassClick, isUserBooked, isUserOnWaitlist }) => {
  const days = getNextTwentyEightDays();

  return (
    <div className="bg-card rounded-xl shadow-sm border border-primary p-6">
      {/* Header */}
      <div className="flex flex-col items-center justify-center mb-6">
        <h2 className="text-lg font-semibold text-primary">
          {getCalendarHeaderText()}
        </h2>
        <p className="text-sm text-secondary mt-1">Next 28 Days</p>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-grid grid gap-1 border border-primary rounded-lg overflow-hidden">
        {days.map((date, index) => (
          <CalendarDay
            key={index}
            date={date}
            index={index}
            classes={classes}
            onClassClick={onClassClick}
            isUserBooked={isUserBooked}
            isUserOnWaitlist={isUserOnWaitlist}
          />
        ))}
      </div>
    </div>
  );
};

export default CalendarView;
